import { useState } from "react";
import { DataWrapper } from "@/common/components/dataWrapper";
import Form from "@/common/components/form/Form";
import Stepper from "@/common/components/stepper";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { KeyValueObj } from "@/common/types";
import { inventoryStep1Form } from "./data";
import { BULK_UPLOAD_NUDGE_THRESHOLD, buildInventorySkeleton, globalProgress } from "./serialAccelerators";
import { IPackagingLayer } from "./inventoryTree";
import { EMPTY_INTAKE_PRICING, IIntakePricing, PricingDetails } from "./PricingDetails";

const INVENTORY_STEPS = [
  { id: 1, label: "Batch details" },
  { id: 2, label: "Serial entry" },
];

interface IInventoryStep1Props {
  productOptions: KeyValueObj[];
  productsFetched: KeyValueObj[];
  productsLoading: boolean;
  productsError: any;
  onRetry: () => void;
  onContinue: (values: KeyValueObj, product: KeyValueObj, pricing: IIntakePricing) => void;
  onSwitchToBulkUpload: (productId: string) => void;
  onDownloadTemplate: (productId: string) => void;
  templateLoading: boolean;
}

// Step 1 of the MED-649 complete-batch inventory flow: batch details, plus a
// product-details/hierarchy summary and a bulk-upload nudge, both shown once
// a product is selected — before the form fields below them.
export function InventoryStep1({
  productOptions,
  productsFetched,
  productsLoading,
  productsError,
  onRetry,
  onContinue,
  onSwitchToBulkUpload,
  onDownloadTemplate,
  templateLoading,
}: IInventoryStep1Props) {
  const [selectedProductId, setSelectedProductId] = useState("");
  // MED-686: pricing is captured here, alongside the batch details, and travels
  // with them to the submit step.
  const [pricing, setPricing] = useState<IIntakePricing>(EMPTY_INTAKE_PRICING);
  const notify = useNotification();

  const selectedProduct = productsFetched?.find((p) => p.id === selectedProductId);
  const layers: KeyValueObj[] = selectedProduct?.packaging_layers || [];
  // MED-649 review: was base_unit_count + max(layers.length - 1, 0), which
  // undercounts (38 instead of 40 for a 1+3+36 hierarchy) — derive from the
  // same skeleton InventoryStep2 actually builds and submits against.
  const totalSerials: number = selectedProduct && layers.length > 0
    ? globalProgress(buildInventorySkeleton(layers as IPackagingLayer[])).total
    : 0;
  const suggestBulkUpload = totalSerials > BULK_UPLOAD_NUDGE_THRESHOLD;

  function handleFieldChange(name: string, value: any) {
    if (name === "product_id") setSelectedProductId(value);
  }

  // MED-649 review: expiry-on/before-manufacture-date was never validated on
  // either side — the declarative rules engine's `notBefore` doesn't support
  // comparing against another field's live value here (its `expectedValue ||
  // formValues[expectedValue]` always takes the literal branch once a value
  // is set), so this is checked directly before advancing to step 2.
  function handleContinue(values: KeyValueObj) {
    const product = productsFetched?.find((p) => p.id === values.product_id);
    if (!product?.packaging_layers?.length) return;

    const manufactureDate = new Date(values.manufacture_date);
    const expiryDate = new Date(values.expiry_date);
    if (expiryDate <= manufactureDate) {
      notify({ type: "ERROR", message: "Expiry date must be after manufacture date" });
      return;
    }

    // MED-686: the pricing fields sit outside the declarative Form (they are
    // fetched and pre-populated per product), so their required-ness is checked
    // here rather than by the rules engine. The server enforces the same rules —
    // this is only to avoid making the user walk through Step 2 to find out.
    if (!pricing.complete) {
      notify({
        type: "ERROR",
        message: pricing.firstPricing
          ? "Enter a selling price — it is required the first time you add this product"
          : "Enter the pricing details for this batch",
      });
      return;
    }

    onContinue(values, product, pricing);
  }

  const formInputs = [
    {
      inputType: "selectInput",
      prompt: "Select the product",
      name: "product_id",
      placeholder: "Select a product from the list",
      fullWidth: false,
      returnId: true,
      options: productOptions,
    },
    ...inventoryStep1Form,
  ];

  return (
    <DataWrapper
      noDataMessage="No product added yet, Products added will show here."
      loading={productsLoading}
      error={productsError}
      onFailAction={onRetry}
      loadingMessage="Loading products data..."
      failedMessage="Could not fetch products data. Check your network and click the button below to retry"
    >
      {/* A single wrapping div, not a fragment: ModalFormWrapper is a flex
          column with a large `gap` between DIRECT children, and a fragment
          is transparent to that — it would apply the gap between the
          stepper, hierarchy card, and form individually instead of once
          around this whole block. The div also gets an explicit width since
          ModalFormWrapper has align-items: center (no stretch), which would
          otherwise shrink it to fit its content. */}
      <div className="w-full">
        <div className="mb-3 w-full">
          <Stepper steps={INVENTORY_STEPS} currentStep={0} />
        </div>

        {selectedProduct && layers.length > 0 && (
          <div className="mb-3 w-full rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-sm font-semibold text-gray-800">
                {selectedProduct.brand_name}
                {selectedProduct.strength ? ` — ${selectedProduct.strength}${selectedProduct.strength_unit === "grams" ? "mg" : "g"}` : ""}
              </span>
              <span className="whitespace-nowrap text-xs font-medium text-blue-700">
                {totalSerials} serial{totalSerials === 1 ? "" : "s"} needed
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
              {/* MED-677: the count is how many of THIS level its parent
                  contains, so it comes from the previous layer's
                  containment_quantity, not this layer's own — the top level
                  is never numbered since nothing holds it. */}
              {layers.map((layer, idx) => (
                <span key={idx} className="flex items-center gap-1.5">
                  <span className="rounded-full bg-white px-2 py-0.5 font-medium capitalize text-gray-700 shadow-sm">
                    {layer.packaging_type}
                    {idx > 0 && layers[idx - 1].containment_quantity
                      ? ` (${layers[idx - 1].containment_quantity})`
                      : ""}
                  </span>
                  {idx < layers.length - 1 && <span className="text-gray-400">→</span>}
                </span>
              ))}
            </div>

            {suggestBulkUpload && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                <span>
                  This batch needs {totalSerials} serials — bulk file upload may be faster for a batch this size.
                </span>
                <div className="flex shrink-0 gap-3">
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => onDownloadTemplate(selectedProduct.id)}
                    disabled={templateLoading}
                  >
                    {templateLoading ? "Preparing..." : "Download template"}
                  </button>
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => onSwitchToBulkUpload(selectedProduct.id)}
                  >
                    Switch to bulk upload
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MED-686: between the hierarchy summary and the batch fields, so the
            step reads product -> what it costs and sells for -> batch facts. */}
        {selectedProductId && (
          <PricingDetails productId={selectedProductId} value={pricing} onChange={setPricing} />
        )}

        <Form
          loadingState={false}
          submitLabel="Continue"
          formInputs={formInputs}
          processInputs={handleContinue}
          spinnerComponent="spinner"
          login={false}
          biControl={false}
          handleBack={undefined}
          externalUpdateData={handleFieldChange}
        />
      </div>
    </DataWrapper>
  );
}
